import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DenseMarketFiltersDesktop,
  DenseMarketFiltersMobile,
  type DenseMarketStatus,
} from "@/components/v2/DenseMarketFilters";
import { DenseMarketTable } from "@/components/v2/DenseMarketTable";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { useNormalizedMarkets } from "@/hooks/useNormalizedMarkets";
import { groupMarketsByAsset } from "@/lib/market-grouping";
import type { Hip3Market } from "@/types/hip3";

function parseStatus(value: string | null): DenseMarketStatus {
  if (value === "all" || value === "active" || value === "inactive") {
    return value;
  }
  return "active";
}

function sortMarkets(rows: Hip3Market[], sort: string) {
  if (sort === "oi") {
    return rows.sort((a, b) => (b.oiUsd ?? 0) - (a.oiUsd ?? 0));
  }
  if (sort === "spread") {
    return rows.sort((a, b) => (a.spreadBps ?? Infinity) - (b.spreadBps ?? Infinity));
  }
  if (sort === "funding") {
    return rows.sort((a, b) => Math.abs(b.funding ?? 0) - Math.abs(a.funding ?? 0));
  }
  if (sort === "score") {
    return rows.sort((a, b) => (b.marketScore ?? 0) - (a.marketScore ?? 0));
  }
  return rows.sort((a, b) => (b.volume24h ?? 0) - (a.volume24h ?? 0));
}

export function MarketsPage() {
  const marketsQuery = useNormalizedMarkets();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = {
    query: searchParams.get("q") ?? "",
    status: parseStatus(searchParams.get("status")),
    sort: searchParams.get("sort") ?? "volume",
    categories: searchParams.getAll("category"),
    providers: searchParams.getAll("provider"),
  };

  const availableCategories = useMemo(
    () => Array.from(new Set(marketsQuery.markets.map((market) => market.category ?? "General"))).sort(),
    [marketsQuery.markets]
  );

  const availableProviders = useMemo(
    () => Array.from(new Set(marketsQuery.markets.map((market) => market.provider))).sort(),
    [marketsQuery.markets]
  );

  const setFilterParams = (next: typeof filters) => {
    const params = new URLSearchParams();
    if (next.query.trim()) params.set("q", next.query.trim());
    if (next.status !== "active") params.set("status", next.status);
    if (next.sort !== "volume") params.set("sort", next.sort);
    next.categories.forEach((value) => params.append("category", value));
    next.providers.forEach((value) => params.append("provider", value));
    setSearchParams(params, { replace: true });
  };

  const filteredMarkets = useMemo(() => {
    const term = filters.query.trim().toLowerCase();

    return sortMarkets([...marketsQuery.markets], filters.sort).filter((market) => {
      if (filters.status === "active" && !market.isActive) return false;
      if (filters.status === "inactive" && market.isActive) return false;

      if (filters.categories.length) {
        const category = market.category ?? "General";
        if (!filters.categories.includes(category)) return false;
      }

      if (filters.providers.length && !filters.providers.includes(market.provider)) return false;

      if (term) {
        const haystack = `${market.baseAsset} ${market.quoteAsset} ${market.marketId} ${market.provider}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [marketsQuery.markets, filters]);

  const groupedMarkets = useMemo(
    () => groupMarketsByAsset(filteredMarkets, (rows) => sortMarkets(rows, filters.sort)),
    [filteredMarkets, filters.sort]
  );

  return (
    <div className="space-y-6">
      <SectionBlock
        title="HIP-3 Markets"
      >
        <div className="grid w-full gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="hidden lg:block min-w-0">
            <DenseMarketFiltersDesktop
              filters={filters}
              categories={availableCategories}
              providers={availableProviders}
              sortOptions={[
                { value: "volume", label: "24h Volume" },
                { value: "oi", label: "Open Interest" },
                { value: "spread", label: "Tightest Spread" },
                { value: "funding", label: "Funding Magnitude" },
                { value: "score", label: "Market Score" },
              ]}
              onChange={setFilterParams}
              onReset={() =>
                setFilterParams({
                  query: "",
                  status: "active",
                  sort: "volume",
                  categories: [],
                  providers: [],
                })
              }
            />
          </div>

          <div className="space-y-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[0.7rem] uppercase tracking-wide text-muted-foreground font-mono">
                Showing {filteredMarkets.length} markets across {groupedMarkets.length} assets
              </div>

              <DenseMarketFiltersMobile
                filters={filters}
                categories={availableCategories}
                providers={availableProviders}
                sortOptions={[
                  { value: "volume", label: "24h Volume" },
                  { value: "oi", label: "Open Interest" },
                  { value: "spread", label: "Tightest Spread" },
                  { value: "funding", label: "Funding Magnitude" },
                  { value: "score", label: "Market Score" },
                ]}
                onChange={setFilterParams}
                onReset={() =>
                  setFilterParams({
                    query: "",
                    status: "active",
                    sort: "volume",
                    categories: [],
                    providers: [],
                  })
                }
              />
            </div>

            <DenseMarketTable groupedMarkets={groupedMarkets} />
          </div>
        </div>
      </SectionBlock>
    </div>
  );
}
